import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useTheme } from "@mui/material"
import fp from "lodash/fp"

import type {
  SpanPlotDef,
  LinePlotView,
  PlotView,
  TableStatPlotView,
} from "types/dashboard.types"
import type { LabelAlign } from "types/viewProperties.types"
import type { Span } from "types/spans.types"

import useSpan from "helpers/hooks/spans/useSpan"
import { axisToFormatterProps, getFormatter } from "widgets/plots/configuration/helpers"

interface SpanReference {
  field: keyof Span
  labelKey: string
  /** A ceiling reads in the error colour; a nominal value is not one. */
  isLimit?: boolean
}

const REFERENCES_BY_NAME_KEY: Record<string, SpanReference[]> = {
  SPAN_LONG_TERM_AMPACITY: [
    { field: "ampacity_nominal", labelKey: "plots_references.AMPACITY_NOMINAL" },
  ],
  SPAN_CONDUCTOR_TEMPERATURE: [
    {
      field: "temperature_max",
      labelKey: "spans.TEMPERATURE_MAX",
      isLimit: true,
    },
    {
      field: "emergency_temperature_max",
      labelKey: "spans.EMERGENCY_TEMPERATURE_MAX",
      isLimit: true,
    },
  ],
}

const THRESHOLD_BY_NAME_KEY: Record<string, keyof Span> = {
  SPAN_BELOW_NOMINAL_AMPACITY: "ampacity_nominal",
}

interface UseSpanPlotViewReturn {
  view: PlotView
}

/**
 * The span's own values folded into its plots, read from the span rather than
 * hardcoded: charts draw them as reference lines, stats measure against them.
 * A plot with no entry above, or a span with the value unset, renders unchanged.
 */
export function useSpanPlotView(
  def: SpanPlotDef,
  spanId?: number,
): UseSpanPlotViewReturn {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage
  const theme = useTheme()

  const references = REFERENCES_BY_NAME_KEY[def.view.nameKey]
  const thresholdField = THRESHOLD_BY_NAME_KEY[def.view.nameKey]
  // The page already fetched this span, so the query resolves from cache.
  const { span } = useSpan({ id: references || thresholdField ? spanId : undefined })

  const view = useMemo((): PlotView => {
    if (thresholdField && def.view.type === "table-stat") {
      const threshold = span?.[thresholdField]

      return typeof threshold === "number"
        ? fp.merge(def.view as TableStatPlotView, { properties: { threshold } })
        : def.view
    }

    const drawn = (references ?? []).flatMap((reference) => {
      const value = span?.[reference.field]
      return typeof value === "number" ? [{ reference, value }] : []
    })

    if (!drawn.length || def.view.type !== "xy") {
      return def.view
    }

    const viewAsXY = def.view as LinePlotView
    const formatter = getFormatter(
      axisToFormatterProps(viewAsXY.properties.axes.y, viewAsXY.properties, locale),
    )
    const values = drawn.map(({ value }) => value)

    return fp.merge(viewAsXY, {
      properties: {
        // Keeps the lines inside the plot when every value sits on one side.
        axes: { y: { bounds: [`${Math.min(...values)}`, `${Math.max(...values)}`] } },
        yReferenceLines: [
          ...(viewAsXY.properties?.yReferenceLines ?? []),
          ...drawn.map(({ reference, value }) => {
            const color = reference.isLimit
              ? theme.palette.error.main
              : theme.palette.text.secondary

            return {
              y: value,
              label: `${t(reference.labelKey)} ${formatter(value)}`,
              labelAlign: "start" as LabelAlign,
              labelStyle: {
                fill: color,
                fontSize: "0.7rem",
                paintOrder: "stroke",
                stroke: theme.palette.background.paper,
                strokeWidth: 3,
              },
              lineStyle: {
                strokeDasharray: "4 4",
                strokeWidth: 0.75,
                stroke: color,
                shapeRendering: "geometricPrecision",
              },
            }
          }),
        ],
      },
    })
  }, [def.view, references, thresholdField, span, locale, t, theme.palette])

  return { view }
}
